import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Table } from 'react-bootstrap';
import styles from './chatbot.module.scss';

const sourceHeadingStyle = {
    fontSize: '0.70rem',
    fontWeight: 'bold',
    color: '#6c757d',
    marginBottom: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
};

const SolutionCard = ({ source, onGoToIdeas }) => {
    return (
        <div className={styles.solutionCard}>
            <div className={styles.solutionTitle}>{source.source}</div>
            <div className={styles.solutionSubtitle}>{source.industry_fit || 'N/A'}</div>
            
            <div className={styles.widgetRow}>
                <div className={styles.widget} title="Documents/Articles">
                    <i className="bi bi-file-earmark-text"></i>
                    <span>{source.document_links?.length || 0}</span>
                </div>
                <div className={styles.widget} title="Video Links">
                    <i className="bi bi-camera-video"></i>
                    <span>{source.video_links?.length || 0}</span>
                </div>
                <div className={styles.widget} title="Website Links">
                    <i className="bi bi-link-45deg"></i>
                    <span>{source.website_links?.length || 0}</span>
                </div>
                <div className={styles.widget} title="Other Artifacts">
                    <i className="bi bi-box-seam"></i>
                    <span>{source.artifact_links?.length || 0}</span>
                </div>
            </div>

            <div className={styles.tagRow}>
                {source.capability_tags?.map((tag, i) => (
                    <span key={i} className={styles.tag}>{tag}</span>
                ))}
            </div>

             <div className="mt-3 text-end">
                <button 
                    className="btn btn-outline-primary btn-sm rounded-pill px-3"
                    style={{ fontSize: '0.75rem', fontWeight: '600' }}
                    onClick={() => onGoToIdeas(source.uuid)}
                >
                    View Details
                </button>
            </div>
        </div>
    );
};

const SolutionGroup = ({ sources, onGoToIdeas }) => {
    const [expandedSourceIndex, setExpandedSourceIndex] = React.useState(null);

    if (!sources || sources.length === 0) return null;

    return (
        <div className="mt-3">
            <div className={styles.sourcePillContainer}>
                {sources.map((source, sIdx) => (
                    <div 
                        key={sIdx} 
                        className={`${styles.sourcePill} ${expandedSourceIndex === sIdx ? styles.active : ''}`}
                        onClick={() => setExpandedSourceIndex(expandedSourceIndex === sIdx ? null : sIdx)}
                    >
                        <div className={styles.statusDot}></div>
                        <span className={styles.sourceName}>{source.source}</span>
                        {source.score && (
                            <span className={styles.matchScore}>{Math.round(source.score * 100)}% Match</span>
                        )}
                    </div>
                ))}
            </div>
            {expandedSourceIndex !== null && (
                <div className="animate-in fade-in slide-in-from-top-2 duration-200">
                    <SolutionCard source={sources[expandedSourceIndex]} onGoToIdeas={onGoToIdeas} />
                </div>
            )}
        </div>
    );
};

const ChatMessages = ({ messages, isLoading, formatTime, messagesEndRef, onGoToIdeas, onFeedback }) => {
    return (
        <div className={styles.messageList}>
            {messages.map((msg, idx) => {
                const isBot = msg.role === 'assistant';

                return (
                    <div key={idx} className={`${styles.messageWrapper} ${isBot ? styles.botWrapper : styles.userWrapper}`}>
                        {isBot ? (
                            <div className={styles.botMessageContent}>
                                <div className={`${styles.messageBubble} ${styles.botBubble}`}>
                                    <div className={styles.msgText}>
                                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                                        <SolutionGroup sources={msg.sources} onGoToIdeas={onGoToIdeas} />
                                    </div>
                                    <div className={styles.msgTime}>{formatTime(msg.timestamp)}</div>
                                </div>
                                {msg.id && onFeedback && (
                                    <div className={styles.feedbackContainer}>
                                        <button 
                                            className={`${styles.feedbackBtn} ${msg.feedback === 1 ? styles.activePositive : ''}`}
                                            onClick={() => onFeedback(msg.id, 1)}
                                            title="Helpful"
                                        >
                                            <i className={`bi ${msg.feedback === 1 ? 'bi-hand-thumbs-up-fill' : 'bi-hand-thumbs-up'}`}></i>
                                        </button>
                                        <button 
                                            className={`${styles.feedbackBtn} ${msg.feedback === -1 ? styles.activeNegative : ''}`}
                                            onClick={() => onFeedback(msg.id, -1)}
                                            title="Not helpful"
                                        >
                                            <i className={`bi ${msg.feedback === -1 ? 'bi-hand-thumbs-down-fill' : 'bi-hand-thumbs-down'}`}></i>
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className={`${styles.messageBubble} ${styles.userBubble}`}>
                                <div className={styles.msgText}>{msg.content}</div>
                                <div className={styles.msgTime}>{formatTime(msg.timestamp)}</div>
                            </div>
                        )}
                    </div>
                );
            })}

            {isLoading && (
                <div className={`${styles.messageWrapper} ${styles.botWrapper}`}>
                    <div className={`${styles.messageBubble} ${styles.botBubble}`}>
                        <div className={styles.typingIndicator}>
                            <span></span><span></span><span></span>
                        </div>
                    </div>
                </div>
            )}
            <div ref={messagesEndRef} />
        </div>
    );
};

export default ChatMessages;